import "./navbar.sass";

import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";

import iNavbarLogoBlue from "../../../assets/icons/navbar-logo-blue.svg";
import iNavbarLogoWhite from "../../../assets/icons/navbar-logo-white.svg";
import { useWidth } from "../../../hooks/use-width";
import { useScrollContext } from "../../../lib/scroll";
import { paths } from "../../../routes/paths";

import BurgerMenu from "./burger-menu/burger-menu";
import NavbarItem from "./navbar-item/navbar-item";

type Props = {
  theme?: "white" | "blue";
};

const TRANSPARENT_SCROLL_MARGIN = 100;

const Navbar = ({ theme = "white" }: Props) => {
  const scrollContext = useScrollContext();
  const { isDesktopWidth } = useWidth();
  const [isVisible, setIsVisible] = useState(true);
  const [isTransparent, setIsTransparent] = useState(false);
  const isVisibleRef = useRef(true);
  const isTransparentRef = useRef(false);
  const location = useLocation();
  const transparentScrollLimitRef = useRef(0);

  const refreshTransparentScrollLimit = useCallback(() => {
    transparentScrollLimitRef.current = Math.max(
      0,
      window.innerHeight - TRANSPARENT_SCROLL_MARGIN,
    );
  }, []);

  /** Обработчик скролла контейнера. */
  const handleContainerScroll = useCallback(
    (amount: number, delta: number) => {
      const y = Math.round(amount);

      let nextVisible = isVisibleRef.current;
      if (delta < -0.5) {
        nextVisible = true;
      } else if (y > 80 && delta > 0.5) {
        nextVisible = false;
      }
      if (nextVisible !== isVisibleRef.current) {
        isVisibleRef.current = nextVisible;
        setIsVisible(nextVisible);
      }

      if (location.pathname !== paths.MainPage) {
        if (isTransparentRef.current) {
          isTransparentRef.current = false;
          setIsTransparent(false);
        }
        return;
      }

      const limit = transparentScrollLimitRef.current;
      const nextTransparent = y < limit;
      if (nextTransparent !== isTransparentRef.current) {
        isTransparentRef.current = nextTransparent;
        setIsTransparent(nextTransparent);
      }
    },
    [location.pathname],
  );

  useEffect(() => {
    refreshTransparentScrollLimit();
    const vv = window.visualViewport;
    window.addEventListener("resize", refreshTransparentScrollLimit);
    vv?.addEventListener("resize", refreshTransparentScrollLimit);
    return () => {
      window.removeEventListener("resize", refreshTransparentScrollLimit);
      vv?.removeEventListener("resize", refreshTransparentScrollLimit);
    };
  }, [refreshTransparentScrollLimit]);

  useEffect(() => {
    scrollContext?.bindScrollHandler(handleContainerScroll);
    return () => {
      scrollContext?.unbindScrollHandler(handleContainerScroll);
    };
  }, [handleContainerScroll, scrollContext]);

  /** При смене страницы: не главная — всегда непрозрачный; главная — по текущему скроллу. */
  useEffect(() => {
    if (location.pathname !== paths.MainPage) {
      isTransparentRef.current = false;
      setIsTransparent(false);
      return;
    }
    refreshTransparentScrollLimit();
    const t = window.scrollY < transparentScrollLimitRef.current;
    isTransparentRef.current = t;
    setIsTransparent(t);
  }, [location.pathname, refreshTransparentScrollLimit]);

  /** Обработчик mousemove события на сенсоре навбара. */
  const handleNavbarSensorMouseMove = useCallback(() => {
    if (!isVisibleRef.current) {
      isVisibleRef.current = true;
      setIsVisible(true);
    }
  }, []);

  return (
    <div
      className={`navbar navbar-${theme} ${
        isVisible ? "navbar-visible" : "navbar-hidden"
      } ${isTransparent ? "navbar-transparent" : ""}`}
    >
      {theme === "white" && (
        <Link to={paths.MainPage}>
          <img src={iNavbarLogoBlue} className="navbar__logo" alt="" />
        </Link>
      )}
      {theme === "blue" && (
        <Link to={paths.MainPage}>
          <img src={iNavbarLogoWhite} className="navbar__logo" alt="" />
        </Link>
      )}

      {isDesktopWidth && (
        <div className="navbar__menu">
          <NavbarItem title="о Нас" link={paths.AboutUsPage} theme={theme} />
          <NavbarItem title="услуги" link={paths.ServicesPage} theme={theme} />
          <NavbarItem title="Кейсы" link={paths.CasesPage} theme={theme} />
          <NavbarItem
            title="тендерхаб"
            link={paths.TenderhubPage}
            theme={theme}
          />
          <NavbarItem
              title="Шоурил"
              isBacklink={true}
              link={paths.ShowreelPage}
              theme={theme}
          />
          <NavbarItem
            title="контакты"
            link={paths.ContactsPage}
            theme={theme}
          />
        </div>
      )}

      <BurgerMenu theme={theme} />

      <div
        className="navbar__sensor"
        onMouseMove={handleNavbarSensorMouseMove}
      />
    </div>
  );
};

export default Navbar;
